import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { CHECKBOX_LABELS, COGNIZANT_AI_LAB_URL, NEURO_SAN_URL } from "../constants";
import { HelpIcon } from "../styles/HelpIcon";
import { LightTooltip } from "../styles/Tooltips";
import { landingPalette } from "../theme";
// #region: Styled Components
const FullWidthButton = styled("button")(({ theme }) => `
    width: 100%;
    padding: 10px;
    margin-bottom: 8px;
    border: none;
    border-radius: 4px;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s, box-shadow 0.2s;

    &.primary {
        background-color: ${theme.palette.primary.main};
        color: ${theme.palette.primary.contrastText};

        &:hover:not(:disabled) {
            background-color: ${landingPalette.buttonHover};
            box-shadow: ${landingPalette.buttonHoverShadow};
        }
    }

    &.secondary {
        background-color: ${theme.palette.secondary.main};
        color: ${theme.palette.secondary.contrastText};

        &:hover:not(:disabled) {
            background-color: ${landingPalette.buttonHover};
            box-shadow: ${landingPalette.buttonHoverShadow};
        }
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`);
const CheckboxGroup = styled(Box) `
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
const CheckboxLabel = styled("label") `
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    cursor: pointer;

    input[type="checkbox"] {
        margin-right: 8px;
    }
`;
const CollapseHeader = styled(Box) `
    display: flex;
    align-items: center;
    justify-content: space-between;
`;
const FormGroup = styled(Box) `
    margin-bottom: 16px;
`;
const HelpText = styled(Box)(({ theme }) => `
    font-size: 0.8rem;
    color: ${theme.palette.text.secondary};
    line-height: 1.4;
    margin-top: 4px;
`);
const HelpTextBlock = styled(HelpText)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));
const Input = styled("input")(({ theme }) => `
    width: 100%;
    padding: 8px;
    border: 1px solid ${theme.palette.divider};
    border-radius: 4px;
    background-color: ${theme.palette.background.default};
    color: ${theme.palette.text.primary};
    font-size: 0.9rem;

    &::placeholder {
        color: ${theme.palette.text.secondary};
    }

    &:focus {
        outline: none;
        border-color: ${theme.palette.primary.main};
        background-color: ${theme.palette.action.hover};
    }
`);
const Label = styled("label")(({ theme }) => `
    display: block;
    font-size: 0.9rem;
    margin-bottom: 4px;
    color: ${theme.palette.text.secondary};
`);
const ModeSwitch = styled(Switch)(({ theme }) => `
    color: ${theme.palette.primary.main};

    & .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
        opacity: 0.35;
    }

    & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
        opacity: 0.8;
    }
`);
const ObjectiveColumn = styled(Box) `
    flex: 1;
`;
const ObjectivesRow = styled(Box) `
    display: flex;
    gap: 12px;
`;
const Section = styled(Box) `
    margin-bottom: 24px;
`;
const SectionHeader = styled(Box) `
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;
const SectionTitle = styled("h3")(({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${theme.palette.text.primary};
    font-size: 1.1rem;
    margin: 0 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid ${theme.palette.divider};
`);
const SidebarContainer = styled(Box, { shouldForwardProp: (prop) => prop !== "isOpen" })(({ theme, isOpen }) => ({
    position: "fixed",
    top: 56,
    left: 0,
    width: 320,
    height: "calc(100vh - 56px)",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: 20,
    overflowY: "auto",
    boxSizing: "border-box",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 24px rgba(12,20,30,0.06)",
    transform: isOpen ? "translateX(0)" : "translateX(-100%)",
    opacity: isOpen ? 1 : 0,
    transition: "transform 240ms ease, opacity 200ms ease",
    pointerEvents: isOpen ? "auto" : "none",
}));
const SmallButton = styled("button")(({ theme }) => `
    padding: 6px 10px;
    font-size: 0.85rem;
    border-radius: 8px;
    border: 1px solid ${theme.palette.divider};
    cursor: pointer;
    background: ${theme.palette.background.default};
    color: ${theme.palette.text.primary};
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    font-weight: 600;
    transition: background-color 0.12s, transform 0.06s;
    &:hover {
        background: ${theme.palette.action.hover};
        transform: translateY(-1px);
    }
`);
const StyledLink = styled("a")(() => ({
    marginLeft: "0.25rem",
    color: "inherit",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
}));
const TextArea = styled("textarea")(({ theme }) => `
    width: 100%;
    padding: 8px;
    border: 1px solid ${theme.palette.divider};
    border-radius: 4px;
    background-color: ${theme.palette.background.default};
    color: ${theme.palette.text.primary};
    font-size: 0.9rem;
    resize: vertical;
    min-height: 60px;

    &::placeholder {
        color: ${theme.palette.text.secondary};
    }

    &:focus {
        outline: none;
        border-color: ${theme.palette.primary.main};
        background-color: ${theme.palette.action.hover};
    }
`);
const VersionLabel = styled(Box)(({ theme }) => ({
    marginTop: "auto",
    paddingTop: 12,
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    display: "flex",
    flexDirection: "column",
    gap: 6,
}));
// #endregion: Types
const Sidebar = ({ sessionId, appState, loading, initializeSession, submitHumanIdea, generateAIIdea, generateAIIdeas, calibrateSession, updateCheckbox, updateSuggestionsValue, prefillValues, onClearPrefill, isOpen = false, machineMode, onModeChange, }) => {
    const uiVersion = process.env["REACT_APP_UI_VERSION"] ?? "dev";
    const { register, handleSubmit, setValue, watch, formState: { errors }, } = useForm({
        defaultValues: {
            question: "",
            objective1: "",
            objective2: "",
            context_value: "",
        },
        mode: "onBlur",
    });
    const ErrorText = styled(Box)(({ theme }) => `
        color: ${theme.palette.error.main};
        font-size: 0.85rem;
        margin-top: 6px;
    `);
    useEffect(() => {
        if (prefillValues) {
            setValue("question", prefillValues.question || "");
            setValue("objective1", prefillValues.objective1 || "");
            setValue("objective2", prefillValues.objective2 || "");
            setValue("context_value", prefillValues.context_value || "");
        }
    }, [prefillValues, setValue]);
    // The topic field is registered with react-hook-form, so its ref has to be shared to focus it.
    const { ref: registerQuestion, ...questionField } = register("question", { required: true });
    const questionRef = useRef(null);
    // Opening the panel before a session exists means the visitor came here to type a topic.
    useEffect(() => {
        if (isOpen && !sessionId) {
            questionRef.current?.focus();
        }
    }, [isOpen, sessionId]);
    const [humanIdea, setHumanIdea] = useState("");
    const humanIdeaRef = useRef(null);
    const wasMachineMode = useRef(machineMode);
    // The Scoreboard's prompts to add a human idea flip this prop, and React reuses the AI
    // suggestions textarea for this field, so autoFocus can't stand in for placing the cursor.
    useEffect(() => {
        if (wasMachineMode.current && !machineMode) {
            humanIdeaRef.current?.focus();
        }
        // Tracked so only the switch into human mode focuses, not a mount already in it.
        wasMachineMode.current = machineMode;
    }, [machineMode]);
    const [configOpen, setConfigOpen] = useState(false);
    const suggestionsValue = appState?.suggestions_value ?? "";
    const onInitialize = (data) => {
        initializeSession(data);
    };
    const clearPrefill = () => {
        setValue("question", "");
        setValue("objective1", "");
        setValue("objective2", "");
        setValue("context_value", "");
        if (typeof onClearPrefill === "function") {
            onClearPrefill();
        }
    };
    // show clear button if there are any values in the form OR prefillValues exists
    const watched = watch(["question", "objective1", "objective2", "context_value"]);
    const anyFormValue = Object.values(watched || {}).some((v) => v != null && (typeof v === "string" ? v.trim() !== "" : true));
    const showClear = !sessionId && (Boolean(prefillValues) || anyFormValue);
    const onSubmitHumanIdea = () => {
        if (humanIdea.trim()) {
            submitHumanIdea(humanIdea.trim());
            setHumanIdea("");
        }
    };
    const onCheckboxChange = (label, checked) => {
        updateCheckbox(label, checked);
    };
    const checkedCount = CHECKBOX_LABELS.filter((l) => appState?.checkbox[l]).length;
    const evalTooltip = [
        "How should answers to this brainstorming topic be evaluated?",
        "List two evaluation objectives (e.g., novelty, feasibility, cost-effectiveness).",
    ].join(" ");
    const contextTooltip = [
        "Provide any additional information that may be helpful to ground the brainstorming",
        "session. This information will be seen by the idea evaluator LLM and the generator (if",
        "enabled).",
    ].join(" ");
    return (_jsxs(SidebarContainer, { isOpen: isOpen, "aria-hidden": !isOpen, children: [_jsxs(Section, { children: [sessionId ? (_jsx(SectionTitle, { children: "Brainstorming topic" })) : (_jsxs(SectionHeader, { children: [_jsx(SectionTitle, { children: "Problem Setup" }), showClear && (_jsx(SmallButton, { onClick: clearPrefill, type: "button", "aria-label": "Clear form values", children: "Clear" }))] })), !sessionId ? (_jsxs("form", { onSubmit: handleSubmit(onInitialize), children: [_jsxs(FormGroup, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center" }, children: [_jsx(Label, { children: "Brainstorming topic" }), _jsx(LightTooltip, { arrow: true, title: evalTooltip, children: _jsx(HelpIcon, { "aria-label": "Brainstorming topic help" }) })] }), _jsx(TextArea, { ...questionField, ref: (el) => {
                                            registerQuestion(el);
                                            questionRef.current = el;
                                        }, placeholder: "Describe the brainstorming topic (no single correct answer)" }), errors.question && _jsx(ErrorText, { children: "Brainstorming topic is required." })] }), _jsx(HelpText, { children: "How should answers to this brainstorming topic be evaluated? List two evaluation objectives below (e.g., novelty, feasibility, cost-effectiveness)." }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Objective 1" }), _jsx(Input, { ...register("objective1", { required: true }), placeholder: "First evaluation objective (e.g., novelty, impact)" }), errors.objective1 && _jsx(ErrorText, { children: "Objective 1 is required." })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Objective 2" }), _jsx(Input, { ...register("objective2", { required: true }), placeholder: "Second evaluation objective (e.g., feasibility, cost)" }), errors.objective2 && _jsx(ErrorText, { children: "Objective 2 is required." })] }), _jsxs(FormGroup, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center" }, children: [_jsx(Label, { children: "Context (optional)" }), _jsx(LightTooltip, { arrow: true, title: contextTooltip, children: _jsx(HelpIcon, { "aria-label": "Context help" }) })] }), _jsx(TextArea, { ...register("context_value"), placeholder: "Provide any additional information that may be helpful..." })] }), _jsx(FullWidthButton, { "data-testid": "sidebar-start_session-btn", type: "submit", className: "primary", disabled: loading, children: "Start Session" })] })) : (_jsxs(Box, { children: [_jsx(FormGroup, { children: _jsx(Box, { children: appState?.question || "-" }) }), _jsxs(ObjectivesRow, { children: [_jsxs(ObjectiveColumn, { children: [_jsx(Label, { children: "Objective 1" }), _jsx(Box, { children: appState?.objective1 || "-" })] }), _jsxs(ObjectiveColumn, { children: [_jsx(Label, { children: "Objective 2" }), _jsx(Box, { children: appState?.objective2 || "-" })] })] }), appState?.context_value && (_jsxs(FormGroup, { children: [_jsx(Label, { children: "Context" }), _jsx(Box, { sx: { whiteSpace: "pre-wrap" }, children: appState.context_value })] }))] }))] }), sessionId && (_jsxs(Section, { children: [_jsxs(SectionTitle, { children: [_jsx("span", { children: "Ideation" }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: machineMode ? 600 : 400 }, children: "AI" }), _jsx(ModeSwitch, { size: "small", checked: !machineMode, onChange: () => onModeChange(!machineMode), slotProps: { input: { "aria-label": "Toggle AI ideation" } }, "data-testid": "sidebar-ideation_mode-switch", sx: {
                                            "& .MuiSwitch-track": {
                                                backgroundColor: "primary.main",
                                                opacity: 0.9,
                                            },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                backgroundColor: "primary.main",
                                                opacity: 1,
                                            },
                                            "& .MuiSwitch-switchBase.Mui-checked": {
                                                color: "#fff",
                                            },
                                        } }), _jsx(Typography, { variant: "body2", sx: { fontWeight: machineMode ? 400 : 600 }, children: "Human" })] })] }), !machineMode ? (_jsxs(_Fragment, { children: [_jsxs(HelpTextBlock, { children: ["We suggest starting with ", _jsx("strong", { children: "three" }), " of your own ideas: two very different approaches, and one that falls somewhere in between. Starting with a diverse set of your own ideas is a great way to spark better results."] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Idea" }), _jsx(TextArea, { "data-testid": "sidebar-human_ideation_idea-textarea", ref: humanIdeaRef, value: humanIdea, onChange: (e) => setHumanIdea(e.target.value), onKeyDown: (e) => {
                                            // Enter submits. Shift+enter still starts a new line.
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                if (!loading && humanIdea.trim()) {
                                                    onSubmitHumanIdea();
                                                }
                                            }
                                        }, placeholder: "Enter your idea here, or shift+enter for a new line..." })] }), _jsx(FullWidthButton, { "data-testid": "sidebar-human_ideation_idea_submit-btn", className: "primary", onClick: onSubmitHumanIdea, disabled: loading || !humanIdea.trim(), children: "Submit Idea" })] })) : (_jsxs(_Fragment, { children: [_jsxs(FormGroup, { children: [_jsxs(CollapseHeader, { children: [_jsx(Label, { children: "Configure what information is included in the prompt:" }), _jsx(SmallButton, { onClick: () => setConfigOpen((s) => !s), type: "button", children: configOpen ? "Hide" : `Show (${checkedCount})` })] }), _jsx(Collapse, { in: configOpen, timeout: "auto", children: _jsx(CheckboxGroup, { children: CHECKBOX_LABELS.map((label) => (_jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", checked: appState?.checkbox[label] || false, onChange: (e) => onCheckboxChange(label, e.target.checked) }), label] }, label))) }) })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Additional suggestions (optional)" }), _jsx(TextArea, { value: suggestionsValue, onChange: (e) => updateSuggestionsValue(e.target.value), placeholder: "Provide any additional information to guide AI idea generation...", disabled: loading }), _jsx(HelpText, { children: "Provide any additional information that may be helpful to guide the AI in generating ideas." })] }), _jsx(FullWidthButton, { className: "primary", onClick: () => generateAIIdea("normal"), disabled: loading, children: "Generate Idea" }), _jsx(FullWidthButton, { className: "primary", onClick: () => generateAIIdeas(10), disabled: loading, children: "Generate Many Ideas" }), _jsx(FullWidthButton, { className: "secondary", onClick: () => calibrateSession(), disabled: loading, children: "Generate Extreme Ideas" })] }))] })), _jsxs(VersionLabel, { children: [_jsxs(Box, { children: ["LMX Version: ", uiVersion] }), _jsxs(Box, { sx: {
                            fontSize: "0.75rem",
                            color: "inherit",
                        }, children: ["Brought to you by", _jsx(StyledLink, { href: COGNIZANT_AI_LAB_URL, rel: "noopener noreferrer", target: "_blank", children: "Cognizant's AI Lab" }), ". Powered by", _jsx(StyledLink, { href: NEURO_SAN_URL, rel: "noopener noreferrer", target: "_blank", children: "Cognizant Neuro San" }), "."] })] })] }));
};
export default Sidebar;
